import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../../services/authService';
import EmployeeBottomNavigation from '../../components/EmployeeBottomNavigation';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import api from '../../config/api';
import { useTranslation } from '../../hooks/useTranslation';
import { setLocale } from '../../utils/translations';

const Settings = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const user = authService.getCurrentUser();
    const { t, locale } = useTranslation();
    const [unreadCount, setUnreadCount] = useState(0);

    useEffect(() => {
        loadUnreadCount();
    }, []);

    const loadUnreadCount = async () => {
        const response = await api.get('/notifications/unread-count');
        setUnreadCount(response.data.count);
    };

    const handleBack = () => {
        navigate(-1);
    };

    const handleEditAccount = () => {
        navigate('/employee/edit-profile');
    };

    const handleChangePassword = async () => {
        const dir = locale === 'ar' ? 'rtl' : 'ltr';
        const textAlign = locale === 'ar' ? 'right' : 'left';
        const { value: formValues } = await Swal.fire({
            title: `<div style="font-size: 1.5rem; font-weight: 700; color: #111; margin-bottom: 0.5rem;">🔒 ${t('employee.settings.changePasswordTitle')}</div>`,
            html: `
                <div class="swal2-form-container" style="text-align: ${textAlign}; direction: ${dir}; max-width: 500px; margin: 0 auto;">
                    <div class="form-field-group">
                        <label class="form-label">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                            </svg>
                            ${t('employee.settings.currentPassword')}
                        </label>
                        <input id="current_password" type="password" class="swal2-input-modern" placeholder="${t('employee.settings.currentPasswordPlaceholder')}" style="direction: ${dir}; text-align: ${textAlign};" />
                    </div>
                    <div class="form-field-group">
                        <label class="form-label">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                            </svg>
                            ${t('employee.settings.newPassword')}
                        </label>
                        <input id="new_password" type="password" class="swal2-input-modern" placeholder="${t('employee.settings.newPasswordPlaceholder')}" style="direction: ${dir}; text-align: ${textAlign};" />
                        <small class="form-hint">${t('employee.settings.passwordHint')}</small>
                    </div>
                    <div class="form-field-group">
                        <label class="form-label">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                            </svg>
                            ${t('employee.settings.confirmPassword')}
                        </label>
                        <input id="confirm_password" type="password" class="swal2-input-modern" placeholder="${t('employee.settings.confirmPasswordPlaceholder')}" style="direction: ${dir}; text-align: ${textAlign};" />
                    </div>
                </div>
            `,
            width: '600px',
            padding: '2rem',
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg> ${t('employee.settings.save')}</span>`,
            cancelButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg> ${t('employee.settings.cancel')}</span>`,
            confirmButtonColor: '#608fd3',
            cancelButtonColor: '#6b7280',
            reverseButtons: locale === 'ar',
            customClass: {
                popup: 'swal2-popup-modern',
                title: 'swal2-title-modern',
                htmlContainer: 'swal2-html-container-modern',
                confirmButton: 'swal2-confirm-modern',
                cancelButton: 'swal2-cancel-modern'
            },
            preConfirm: () => {
                const currentPassword = document.getElementById('current_password').value.trim();
                const newPassword = document.getElementById('new_password').value.trim();
                const confirmPassword = document.getElementById('confirm_password').value.trim();

                if (!currentPassword) {
                    Swal.showValidationMessage(`⚠️ ${t('employee.settings.currentPasswordRequired')}`);
                    return false;
                }

                if (!newPassword) {
                    Swal.showValidationMessage(`⚠️ ${t('employee.settings.newPasswordRequired')}`);
                    return false;
                }

                if (newPassword.length < 8) {
                    Swal.showValidationMessage(`⚠️ ${t('employee.settings.newPasswordMinLength')}`);
                    return false;
                }

                if (newPassword !== confirmPassword) {
                    Swal.showValidationMessage(`⚠️ ${t('employee.settings.confirmPasswordMismatch')}`);
                    return false;
                }

                return {
                    current_password: currentPassword,
                    new_password: newPassword,
                    new_password_confirmation: confirmPassword
                };
            }
        });

        if (formValues) {
            try {
                await api.post('/auth/change-password', formValues);
                toast.success(t('employee.settings.passwordChangeSuccess'));
            } catch (error) {
                const message = error.response?.data?.message || t('employee.settings.passwordChangeFailed');
                toast.error(message);
            }
        }
    };

    const handleAboutApp = () => {
        const dir = locale === 'ar' ? 'rtl' : 'ltr';
        const textAlign = locale === 'ar' ? 'right' : 'left';
        Swal.fire({
            title: `<div style="font-size: 1.5rem; font-weight: 700; color: #111;">📱 ${t('employee.settings.aboutAppTitle')}</div>`,
            html: `
                <div style="text-align: ${textAlign}; direction: ${dir}; padding: 1rem 0;">
                    <p style="margin-bottom: 1rem; color: #374151; line-height: 1.6;">
                        ${t('employee.settings.aboutAppDescription')}
                    </p>
                    <p style="margin-bottom: 1rem; color: #374151; line-height: 1.6;">
                        ${t('employee.settings.appVersion')}
                    </p>
                    <p style="color: #374151; line-height: 1.6;">
                        ${t('employee.settings.allRightsReserved')}
                    </p>
                </div>
            `,
            confirmButtonText: t('employee.settings.ok'),
            confirmButtonColor: '#608fd3',
            customClass: {
                popup: 'swal2-popup-modern',
                title: 'swal2-title-modern',
                htmlContainer: 'swal2-html-container-modern',
                confirmButton: 'swal2-confirm-modern'
            }
        });
    };

    const handleTermsOfUse = () => {
        const dir = locale === 'ar' ? 'rtl' : 'ltr';
        const textAlign = locale === 'ar' ? 'right' : 'left';
        Swal.fire({
            title: `<div style="font-size: 1.5rem; font-weight: 700; color: #111;">📄 ${t('employee.settings.termsTitle')}</div>`,
            html: `
                <div style="text-align: ${textAlign}; direction: ${dir}; padding: 1rem 0; max-height: 400px; overflow-y: auto;">
                    <p style="margin-bottom: 1rem; color: #374151; line-height: 1.6;">
                        ${t('employee.settings.termsContent.acceptance')}
                    </p>
                    <p style="margin-bottom: 1rem; color: #374151; line-height: 1.6;">
                        ${t('employee.settings.termsContent.allowedUse')}
                    </p>
                    <p style="margin-bottom: 1rem; color: #374151; line-height: 1.6;">
                        ${t('employee.settings.termsContent.privacy')}
                    </p>
                    <p style="margin-bottom: 1rem; color: #374151; line-height: 1.6;">
                        ${t('employee.settings.termsContent.liability')}
                    </p>
                    <p style="color: #374151; line-height: 1.6;">
                        ${t('employee.settings.termsContent.modifications')}
                    </p>
                </div>
            `,
            confirmButtonText: t('employee.settings.ok'),
            confirmButtonColor: '#608fd3',
            customClass: {
                popup: 'swal2-popup-modern',
                title: 'swal2-title-modern',
                htmlContainer: 'swal2-html-container-modern',
                confirmButton: 'swal2-confirm-modern'
            }
        });
    };

    const handleContactUs = () => {
        const dir = locale === 'ar' ? 'rtl' : 'ltr';
        const textAlign = locale === 'ar' ? 'right' : 'left';
        Swal.fire({
            title: `<div style="font-size: 1.5rem; font-weight: 700; color: #111;">📞 ${t('employee.settings.contactTitle')}</div>`,
            html: `
                <div style="text-align: ${textAlign}; direction: ${dir}; padding: 1rem 0;">
                    <p style="margin-bottom: 1rem; color: #374151; line-height: 1.6;">
                        <strong>${t('employee.settings.email')}</strong><br/>
                        <a href="mailto:support@tawzeef.com" style="color: #608fd3; text-decoration: none;">support@tawzeef.com</a>
                    </p>
                    <p style="margin-bottom: 1rem; color: #374151; line-height: 1.6;">
                        <strong>${t('employee.settings.phone')}</strong><br/>
                        <a href="tel:+966123456789" style="color: #608fd3; text-decoration: none;">+966 12 345 6789</a>
                    </p>
                    <p style="color: #374151; line-height: 1.6;">
                        <strong>${t('employee.settings.address')}</strong><br/>
                        ${t('employee.settings.addressValue')}
                    </p>
                </div>
            `,
            confirmButtonText: t('employee.settings.ok'),
            confirmButtonColor: '#608fd3',
            customClass: {
                popup: 'swal2-popup-modern',
                title: 'swal2-title-modern',
                htmlContainer: 'swal2-html-container-modern',
                confirmButton: 'swal2-confirm-modern'
            }
        });
    };

    const handleDeleteAccount = async () => {
        const dir = locale === 'ar' ? 'rtl' : 'ltr';
        const result = await Swal.fire({
            html: `<div style="text-align: center; direction: ${dir};">
                <p style="font-size: 1.1rem; margin-bottom: 0.5rem;">${t('employee.settings.deleteAccountTitle')}</p>
                <p style="color: #dc2626; font-size: 0.85rem; margin-top: 0.5rem;">${t('employee.settings.deleteAccountWarning')}</p>
            </div>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg> ${t('employee.settings.deleteAccountButton')}</span>`,
            cancelButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg> ${t('employee.settings.cancel')}</span>`,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#6b7280',
            reverseButtons: locale === 'ar',
            customClass: {
                popup: 'swal2-popup-modern',
                confirmButton: 'swal2-confirm-delete-rtl',
                cancelButton: 'swal2-cancel-rtl'
            }
        });

        if (result.isConfirmed) {
            try {
                // TODO: Implement delete account API call
                toast.info(t('employee.settings.deleteAccountInfo'));
            } catch (error) {
                const message = error.response?.data?.message || t('employee.settings.deleteAccountFailed');
                toast.error(message);
            }
        }
    };

    const handleChangeLanguage = () => {
        const newLocale = locale === 'ar' ? 'en' : 'ar';
        setLocale(newLocale);
        toast.success(t('employee.settings.languageChanged'));
        // Force page reload to apply language change
        setTimeout(() => {
            window.location.reload();
        }, 500);
    };

    const handleLogout = async () => {
        const dir = locale === 'ar' ? 'rtl' : 'ltr';
        const result = await Swal.fire({
            html: `<div style="text-align: center; direction: ${dir};">
                <p>${t('employee.settings.logoutTitle')}</p>
            </div>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg> ${t('employee.settings.logoutButton')}</span>`,
            cancelButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg> ${t('employee.settings.cancel')}</span>`,
            confirmButtonColor: '#608fd3',
            cancelButtonColor: '#6b7280',
            reverseButtons: locale === 'ar',
            customClass: {
                popup: 'swal2-popup-modern',
                confirmButton: 'swal2-confirm-modern',
                cancelButton: 'swal2-cancel-modern'
            }
        });

        if (result.isConfirmed) {
            try {
                // Call logout API if available
                try {
                    await api.post('/auth/logout');
                } catch (error) {
                    // If API call fails, still logout locally
                    console.error('Logout API error:', error);
                }

                authService.logout();
                toast.success(t('employee.settings.logoutSuccess'));
                navigate('/login');
            } catch (error) {
                // Even if there's an error, logout locally
                authService.logout();
                navigate('/login');
            }
        }
    };

    const menuItems = [
        {
            id: 'change-language',
            title: t('employee.settings.changeLanguage'),
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#608fd3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="2" y1="12" x2="22" y2="12"></line>
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                </svg>
            ),
            onClick: handleChangeLanguage,
            highlighted: false
        },
        {
            id: 'edit-account',
            title: t('employee.settings.editAccount'),
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#608fd3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                </svg>
            ),
            onClick: handleEditAccount,
            highlighted: true
        },
        {
            id: 'change-password',
            title: t('employee.settings.changePassword'),
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#608fd3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
            ),
            onClick: handleChangePassword
        },
        {
            id: 'about-app',
            title: t('employee.settings.aboutApp'),
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#608fd3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M12 16v-4"></path>
                    <path d="M12 8h.01"></path>
                </svg>
            ),
            onClick: handleAboutApp
        },
        {
            id: 'terms',
            title: t('employee.settings.termsOfUse'),
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#608fd3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
            ),
            onClick: handleTermsOfUse
        },
        {
            id: 'contact',
            title: t('employee.settings.contactUs'),
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#608fd3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                </svg>
            ),
            onClick: handleContactUs
        },
        {
            id: 'delete-account',
            title: t('employee.settings.deleteAccount'),
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#608fd3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                    <line x1="18" y1="8" x2="6" y2="8"></line>
                </svg>
            ),
            onClick: handleDeleteAccount
        },
        {
            id: 'logout',
            title: t('employee.settings.logout'),
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#608fd3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16 17 21 12 16 7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                </svg>
            ),
            onClick: handleLogout
        }
    ];

    return (
        <div className="employee-settings-page" dir="rtl">
            {/* Header */}
            <div className="settings-header d-flex justify-content-between align-items-center px-3 py-3" dir='ltr'>
                <div className="notification-icon">
                    <button
                        onClick={() => navigate('/employee/notifications')}
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: '4px',
                            cursor: 'pointer',
                            position: 'relative'
                        }}
                    >
                        <span className="bell-icon">🔔</span>
                            {unreadCount > 0 && (
                                <span
                                    style={{
                                    position: 'absolute',
                                    top: '-2px',
                                    right: '-2px',
                                    background: '#dc2626',
                                    color: 'white',
                                    borderRadius: '50%',
                                    width: '18px',
                                    height: '18px',
                                    fontSize: '11px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 'bold'
                                    }}
                                >
                                    {unreadCount > 99 ? '99+' : unreadCount}
                                </span>
                            )}
                    </button>
                </div>
                <div className="settings-title">{t('employee.settings.title')}</div>
                <button className="settings-back-btn" onClick={handleBack}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#343330" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                </button>
            </div>

            {/* Menu Items */}
            <div className="settings-menu-container">
                {menuItems.map((item) => (
                    <div
                        key={item.id}
                        className={`settings-menu-item`}
                        onClick={item.onClick}
                    >
                        <div className="settings-menu-text">{item.title}</div>
                        <div className="settings-menu-icon">{item.icon}</div>
                    </div>
                ))}
            </div>

            {/* Bottom Navigation */}
            <EmployeeBottomNavigation />
        </div>
    );
};

export default Settings;

